// src/pages/navbar/CollectionPoints.jsx
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const fadeInUp = {
  hidden: { opacity: 0, y: 35 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.11, delayChildren: 0.15 }
  }
};

const cardHover = {
  rest: {
    y: 0,
    scale: 1,
    boxShadow: "0 10px 25px -5px rgba(0,0,0,0.07), 0 10px 10px -5px rgba(0,0,0,0.04)"
  },
  hover: {
    y: -10,
    scale: 1.03,
    boxShadow: "0 25px 50px -12px rgba(16, 185, 129, 0.25), 0 10px 10px -5px rgba(0,0,0,0.1)",
    transition: { type: "spring", stiffness: 400, damping: 18 }
  }
};

export default function CollectionPoints() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const points = [
    {
      name: "Railway Station Recycling Hub",
      address: "Near Station Road, Jalgaon",
      types: ["Dry Waste", "Plastic", "Paper"],
      timing: "8:00 AM – 8:00 PM",
      distance: "2.4 km",
      color: "from-emerald-500 to-teal-500"
    },
    {
      name: "MIDC Waste Sorting Center",
      address: "Ajanta Road MIDC Area, Jalgaon",
      types: ["Dry Waste", "E-Waste", "Metal"],
      timing: "7:30 AM – 7:00 PM",
      distance: "4.1 km",
      color: "from-teal-500 to-cyan-500"
    },
    {
      name: "Akashwani Chowk Collection Point",
      address: "Akashwani Chowk, Ring Road, Jalgaon",
      types: ["Plastic", "Metal", "Municipal Bin"],
      timing: "24 × 7 Access",
      distance: "3.8 km",
      color: "from-cyan-500 to-emerald-600"
    },
    {
      name: "Mehrun Lake Eco Point",
      address: "Near Mehrun Lake Garden, Jalgaon",
      types: ["Dry Waste", "Bulk Recyclables"],
      timing: "9:00 AM – 6:00 PM",
      distance: "7.2 km",
      color: "from-emerald-600 to-green-700"
    },
    {
      name: "Gandhi Market Dry Waste Center",
      address: "Gandhi Market, Navi Peth, Jalgaon",
      types: ["All Dry Waste", "Bulk Items"],
      timing: "6:00 AM – 10:00 PM",
      distance: "11 km",
      color: "from-green-600 to-emerald-700"
    },
    {
      name: "Paldhi Naka Recovery Point",
      address: "Paldhi Naka Corridor, Jalgaon",
      types: ["Paper", "Plastic", "Clothes"],
      timing: "8:00 AM – 7:00 PM",
      distance: "5.6 km",
      color: "from-emerald-500 to-lime-600"
    },
  ];

  return (
    <div className="relative min-h-screen bg-linear-to-b from-slate-50 via-emerald-50/45 to-white pt-16 pb-24 md:pt-24 md:pb-32 overflow-hidden">
      <div className="pointer-events-none absolute -top-16 left-10 h-72 w-72 rounded-full bg-emerald-300/20 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 rounded-full bg-cyan-300/20 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <motion.div
          className="text-center mb-16 md:mb-20"
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <p className="mb-4 inline-flex items-center rounded-full border border-emerald-200 bg-white/80 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 shadow-sm">
            Location Intelligence
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-gray-900">
            Collection Points{' '}
            <span className="bg-linear-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent">
              Near You
            </span>
          </h1>
          <p className="mt-5 md:mt-6 text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            Locate verified dry waste, plastic, e-waste, metal buy-back and municipal points across Jalgaon
          </p>
        </motion.div>

        {/* Map Section */}
        <motion.div
          className="mt-8 md:mt-12 bg-white/90 backdrop-blur rounded-2xl md:rounded-3xl shadow-2xl shadow-emerald-100/40 overflow-hidden border border-white/70 relative"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <div className="h-80 sm:h-96 md:h-[500px] bg-linear-to-br from-gray-100 to-gray-200 flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(16,185,129,0.08),transparent_40%)]" />

            <div className="text-center px-6 max-w-xl z-10">
              <div className="text-6xl md:text-8xl mb-5 opacity-70">🗺️</div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
                Interactive Jalgaon Map
              </h3>
              <p className="text-lg md:text-xl text-gray-600">
                Showing nearby collection points, distances & types — coming very soon!
              </p>
              <p className="mt-4 text-sm md:text-base text-emerald-700 font-medium">
                (Powered by Google Maps / Leaflet + real-time WasteZero data)
              </p>
            </div>

            {/* Fake pin decorations */}
            <div className="absolute top-1/4 left-1/3 w-5 h-5 bg-emerald-500 rounded-full shadow-lg animate-pulse" />
            <div className="absolute bottom-1/3 right-1/4 w-4 h-4 bg-teal-500 rounded-full shadow-lg animate-pulse delay-300" />
            <div className="absolute top-2/3 left-1/2 w-6 h-6 bg-cyan-500 rounded-full shadow-lg animate-pulse delay-700" />
          </div>
        </motion.div>

        {/* Points Grid */}
        <motion.div
          className="mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          variants={staggerContainer}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {points.map((point) => (
            <motion.div
              key={point.name}
              variants={fadeInUp}
              initial="rest"
              whileHover="hover"
              className="relative group"
            >
              <motion.div
                variants={cardHover}
                className="bg-white/90 backdrop-blur rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-lg shadow-emerald-100/35 border border-white/70 overflow-hidden h-full flex flex-col"
              >
                {/* Gradient top accent */}
                <div className={`absolute top-0 left-0 right-0 h-1.5 bg-linear-to-r ${point.color}`} />

                <div className="flex items-start justify-between mb-5">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight pr-4">
                    {point.name}
                  </h3>
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-sm font-semibold whitespace-nowrap">
                    {point.distance}
                  </span>
                </div>

                <p className="text-gray-600 mb-4 grow">
                  {point.address}
                </p>

                <div className="flex flex-wrap gap-2 mb-5">
                  {point.types.map((type) => (
                    <span
                      key={type}
                      className="px-3 py-1 bg-gray-100 text-gray-700 text-xs md:text-sm rounded-full font-medium"
                    >
                      {type}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 font-medium">Timings:</span>
                  <span className="font-semibold text-emerald-700">{point.timing}</span>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          className="mt-20 md:mt-28 text-center"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.9 }}
        >
          <p className="text-xl md:text-2xl text-gray-800 font-medium mb-8">
            Want pickup tracking and personalized location suggestions?
          </p>

          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <motion.div
              className="inline-flex items-center gap-3 rounded-full bg-linear-to-r from-emerald-600 to-teal-500 px-10 py-5 md:px-12 md:py-5 text-lg md:text-xl font-bold text-white shadow-xl shadow-emerald-200/50 hover:shadow-emerald-300/60 hover:brightness-110 transition-all duration-300"
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.97 }}
            >
              <Link to="/register" className="inline-flex items-center gap-3">
                Create Free Account
                <span className="text-2xl">→</span>
              </Link>
            </motion.div>

            <Link
              to="/opportunities"
              className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-white px-7 py-3.5 text-base font-semibold text-slate-900 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800"
            >
              Explore Opportunities
            </Link>
          </div>

          <p className="mt-6 text-gray-500 text-base">
            Already have an account? <Link to="/login" className="font-semibold text-emerald-700 hover:text-emerald-800">Log in</Link> to use dashboard pickup tools.
          </p>
        </motion.div>
      </div>
    {/* Contact & Social */}
    <div className="mt-16 text-center">
      <div className="text-xs text-slate-400">Email: <a href="mailto:vijayshikhareteam@gmail.com" className="text-emerald-500 hover:underline">vijayshikhareteam@gmail.com</a></div>
      <div className="text-xs text-slate-400">Mobile: <a href="tel:+919422737898" className="text-emerald-500 hover:underline">+91 9422737898</a></div>
      <div className="flex gap-2 mt-1 justify-center">
        <a href="https://instagram.com/shikharecoder" target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-emerald-600">Instagram</a>
        <a href="https://twitter.com/vijayshikhre" target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-emerald-600">Twitter/X</a>
        <a href="https://linkedin.com/in/vijayshikhare" target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-emerald-600">LinkedIn</a>
        <a href="https://github.com/vijayshikhare" target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-emerald-600">GitHub</a>
      </div>
    </div>
    </div>
  );
}